import { useEffect, useState } from "react";

import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { COMPLETION_REASON_SUGGESTIONS } from "features/pregnancy/constants/completionReasons";
import { useCompletePregnancy } from "features/pregnancy/hooks/useCompletePregnancy";

import style from "./CompletePregnancyDialog.module.css";

const REASON_LIST_ID = "pregnancy-completion-reasons";

/**
 * Ending a pregnancy, behind a deliberate step (KIN-1942).
 *
 * A dialog rather than a button that acts on click: this ends the pregnancy and changes what the
 * patient's chart says about her, so it should not be reachable by one misclick. The reason is
 * required by the backend, and asking for it here is also what stops the action being reflexive.
 *
 * The input is free text with a datalist rather than a select — see the suggestions themselves for
 * why there is no closed list to choose from.
 */
export const CompletePregnancyDialog = ({
  open,
  onClose,
  tenantVal,
  pregnancyId,
}) => {
  const [reason, setReason] = useState("");

  const completePregnancy = useCompletePregnancy({
    tenantVal,
    pregnancyId,
    onSuccess: onClose,
  });

  // A reason typed and then abandoned must not be waiting in the box the next time this opens —
  // it would be attached to whatever pregnancy is on screen then.
  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const trimmedReason = reason.trim();

  return (
    <Dialog
      open={open}
      title="Complete pregnancy"
      onClose={onClose}
      width={520}
    >
      <div className={style.body}>
        <p className={style.explanation}>
          This marks the pregnancy as ended and stops it being treated as ongoing
          on this patient&apos;s record. It can be reopened if it is completed by
          mistake.
        </p>

        <label className={style.label} htmlFor="pregnancy-completion-reason">
          Reason
        </label>
        <input
          id="pregnancy-completion-reason"
          className={style.input}
          list={REASON_LIST_ID}
          value={reason}
          placeholder="Live birth"
          autoComplete="off"
          onChange={(event) => setReason(event.target.value)}
        />
        {/* Suggestions, not a closed set — anything remains typeable. */}
        <datalist id={REASON_LIST_ID}>
          {COMPLETION_REASON_SUGGESTIONS.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
        <p className={style.hint}>
          Choose one of the suggestions or write your own.
        </p>
      </div>

      <DialogFooter>
        <Button variant="outline" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!trimmedReason || completePregnancy.isPending}
          onClick={() => completePregnancy.mutate(trimmedReason)}
        >
          {completePregnancy.isPending ? "Completing…" : "Complete pregnancy"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
